import React, { useState, useEffect } from 'react';
import {
 TrophyIcon,
 StarIcon,
 FireIcon,
 ClockIcon,
 ChartBarIcon,
 ArrowTrendingUpIcon,
 BoltIcon,
 UserGroupIcon,
 CheckCircleIcon
} from '@heroicons/react/24/outline';
import {
 TrophyIcon as TrophyIconSolid,
 StarIcon as StarIconSolid,
 FireIcon as FireIconSolid
} from '@heroicons/react/24/solid';
import API_BASE_URL from '../Config/config';

const API = API_BASE_URL;

const TeamScoreWidget = ({ teamId, userId }) => {
 const [teamScore, setTeamScore] = useState(null);
 const [loading, setLoading] = useState(true);
 const [error, setError] = useState('');
 const [lastUpdated, setLastUpdated] = useState(null);

 useEffect(() => {
 if (teamId) {
 fetchTeamScore();
 // Auto-refresh every 30 seconds
 const interval = setInterval(fetchTeamScore, 30000);
 return () => clearInterval(interval);
 }
 }, [teamId]);

 const fetchTeamScore = async () => {
 try {
 setLoading(true);
 console.log('🏆 Fetching team score for teamId:', teamId);
 const response = await fetch(`${API}/api/teams/leaderboard`);
 const data = await response.json();
 
 console.log('🏆 Team leaderboard data:', data);
 
 if (data.success) {
 // Find our team in the leaderboard
 const ourTeam = data.leaderboard.find(team => team.team_id === teamId);
 console.log('🏆 Our team data:', ourTeam);
 
 if (ourTeam) {
 setTeamScore({
 ...ourTeam,
 rank: ourTeam.rank,
 totalTeams: data.total_teams
 });
 } else {
 // If team not in leaderboard, it means 0 score
 setTeamScore({
 rank: data.total_teams + 1,
 totalTeams: data.total_teams,
 team_score: 0,
 completion_percentage: 0,
 completed_phases: 0,
 total_phases: 0,
 member_count: 4
 });
 }
 setLastUpdated(new Date());
 } else {
 setError('Failed to fetch team score');
 }
 } catch (err) {
 setError('Error loading team score');
 console.error('Team score fetch error:', err);
 } finally {
 setLoading(false);
 }
 };

 const getScoreColor = (score) => {
 if (score >= 80) return 'text-green-400';
 if (score >= 60) return 'text-primary';
 if (score >= 40) return 'text-orange-400';
 if (score >= 20) return 'text-blue-400';
 return 'text-muted';
 };

 const getScoreGradient = (score) => {
 if (score >= 80) return 'from-green-400 to-green-600';
 if (score >= 60) return 'from-yellow-400 to-yellow-600';
 if (score >= 40) return 'from-orange-400 to-orange-600';
 if (score >= 20) return 'from-blue-400 to-blue-600';
 return 'from-gray-400 to-gray-600';
 };

 const getRankIcon = (rank) => {
 if (rank === 1) return <TrophyIconSolid className="h-6 w-6 text-secondary" />;
 if (rank === 2) return <TrophyIconSolid className="h-6 w-6 text-muted" />;
 if (rank === 3) return <TrophyIconSolid className="h-6 w-6 text-accent" />;
 return <span className="text-lg font-bold text-gray-600">#{rank}</span>;
 };

 const getMotivationalMessage = (score, rank, totalTeams) => {
 if (score === 0) {
 return {
 message:"Start your journey! Complete your first task to earn points.",
 icon: <BoltIcon className="h-5 w-5 text-blue-400" />,
 color:"text-blue-400"
 };
 }
 
 if (rank <= 3) {
 return {
 message:"🏆 You're in the top 3! Keep up the excellent work!",
 icon: <TrophyIconSolid className="h-5 w-5 text-secondary" />,
 color:"text-secondary"
 };
 }
 
 if (rank <= Math.ceil(totalTeams * 0.5)) {
 return {
 message:"📈 Great progress! You're in the top half!",
 icon: <ArrowTrendingUpIcon className="h-5 w-5 text-green-400" />,
 color:"text-green-400"
 };
 }
 
 return {
 message:"💪 Keep pushing! Every task completed brings you closer to the top!",
 icon: <FireIcon className="h-5 w-5 text-orange-400" />,
 color:"text-orange-400"
 };
 };

 if (loading && !teamScore) {
 return (
 <div className="bg-gray-100 rounded-lg p-6">
 <div className="flex items-center justify-center h-32">
 <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
 </div>
 </div>
 );
 }

 if (error) {
 return (
 <div className="bg-gray-100 rounded-lg p-6">
 <div className="text-center text-red-400">
 <p>{error}</p>
 <button 
 onClick={fetchTeamScore}
 className="mt-2 px-4 py-2 bg-secondary text-black rounded hover:bg-primary transition-colors"
 >
 Retry
 </button>
 </div>
 </div>
 );
 }

 if (!teamScore) return null;

 const motivational = getMotivationalMessage(teamScore.team_score, teamScore.rank, teamScore.totalTeams);

 return (
 <div className="bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-900 rounded-xl p-6 border border-gray-300">
 {/* Header */}
 <div className="flex items-center justify-between mb-4">
 <div className="flex items-center gap-3">
 <div className="p-2 bg-secondary/20 rounded-lg">
 <TrophyIcon className="h-6 w-6 text-primary" />
 </div>
 <div>
 <h3 className="text-lg font-bold text-gray-900">Team Score</h3>
 <p className="text-sm text-gray-600">Performance Dashboard</p>
 </div>
 </div>
 <div className="flex items-center gap-2">
 {getRankIcon(teamScore.rank)}
 <span className="text-sm text-gray-600">of {teamScore.totalTeams}</span>
 </div>
 </div>

 {/* Score Display */}
 <div className="text-center mb-6">
 <div className={`text-4xl font-bold ${getScoreColor(teamScore.team_score)} mb-2`}>
 {teamScore.team_score}
 </div>
 <div className="text-sm text-gray-600 mb-4">Total Points</div>
 
 {/* Progress Bar */}
 <div className="w-full bg-gray-300 rounded-full h-3 mb-2">
 <div 
 className={`h-3 rounded-full bg-gradient-to-r ${getScoreGradient(teamScore.team_score)} transition-all duration-1000`}
 style={{ width: `${Math.min((teamScore.team_score / 100) * 100, 100)}%` }}
 ></div>
 </div>
 <div className="text-xs text-gray-600 mb-2">
 {Math.round((teamScore.team_score / 100) * 100)}% of potential score
 </div>
 
 {/* Manual Sync Button */}
 <button
 onClick={fetchTeamScore}
 className="text-xs bg-blue-600 hover:bg-blue-700 text-white px-2 py-1 rounded transition-colors"
 title="Refresh team score"
 >
 🔄 Sync
 </button>
 </div>

 {/* Stats Grid */}
 <div className="grid grid-cols-2 gap-4 mb-4">
 <div className="bg-gray-200/50 rounded-lg p-3">
 <div className="flex items-center gap-2 mb-1">
 <CheckCircleIcon className="h-4 w-4 text-green-400" />
 <span className="text-xs text-gray-600">Completed</span>
 </div>
 <div className="text-lg font-semibold text-gray-900">
 {teamScore.completed_phases}/{teamScore.total_phases || 0}
 </div>
 <div className="text-xs text-gray-600">Tasks</div>
 </div>
 
 <div className="bg-gray-200/50 rounded-lg p-3">
 <div className="flex items-center gap-2 mb-1">
 <ChartBarIcon className="h-4 w-4 text-blue-400" />
 <span className="text-xs text-gray-600">Progress</span>
 </div>
 <div className="text-lg font-semibold text-gray-900">
 {teamScore.completion_percentage}%
 </div>
 <div className="text-xs text-gray-600">Complete</div>
 </div>
 </div>

 {/* Motivational Message */}
 <div className={`p-3 rounded-lg bg-gray-200/30 border-l-4 border-current ${motivational.color}`}>
 <div className="flex items-center gap-2">
 {motivational.icon}
 <p className={`text-sm ${motivational.color} font-medium`}>
 {motivational.message}
 </p>
 </div>
 </div>

 {/* Last Updated */}
     {lastUpdated && lastUpdated instanceof Date && !isNaN(lastUpdated) && (
       <div className="mt-4 text-center">
         <p className="text-xs text-gray-600">
           Updated: {lastUpdated.toLocaleTimeString()}
         </p>
       </div>
     )}
 </div>
 );
};

export default TeamScoreWidget;



